
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth';
import { toggleStoryLike, hasUserLikedStory } from '@/services/analyticsService';
import { toast } from 'sonner';

export const useStoryLikes = (storyId: string, initialLikes: number) => {
  const { user } = useAuth();
  const [likes, setLikes] = useState(initialLikes);
  const [userHasLiked, setUserHasLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if the current user has liked this story
  useEffect(() => {
    const checkIfLiked = async () => {
      if (!user?.id || !storyId) {
        setUserHasLiked(false);
        return;
      }
      
      try {
        const hasLiked = await hasUserLikedStory(storyId, user.id);
        setUserHasLiked(Boolean(hasLiked)); // Convert to boolean properly
      } catch (error) {
        console.error('Error checking if user liked story:', error);
      }
    };
    
    checkIfLiked();
  }, [user, storyId]);

  // Function to toggle like status
  const toggleLike = async () => {
    if (!user) {
      toast("Authentication required", {
        description: "Please sign in to like stories",
      });
      return;
    }
    
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const isNowLiked = await toggleStoryLike(storyId, user.id);
      setUserHasLiked(Boolean(isNowLiked)); // Convert to boolean properly
      
      // Update the like count
      setLikes(prev => isNowLiked ? prev + 1 : Math.max(0, prev - 1));
      
      toast(isNowLiked ? "Story liked" : "Like removed", {
        description: isNowLiked 
          ? "This story has been added to your likes" 
          : "This story has been removed from your likes",
      });
    } catch (error) {
      console.error('Error toggling story like:', error);
      toast("Error", {
        description: "There was a problem updating your like. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return { likes, userHasLiked, isLoading, toggleLike };
};
